import React, { useState } from 'react';
import { useGetLanguageMultipliersQuery, useUpdateLanguageMultiplierMutation, LanguageMultiplier } from 'store/services/admin/adminApi';
import { getLanguageDisplayName } from 'constants/languages';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';

const LanguageMultiplierSection: React.FC = () => {
  const { data: multipliers, isLoading } = useGetLanguageMultipliersQuery();
  const [updateMultiplier] = useUpdateLanguageMultiplierMutation();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editMultiplier, setEditMultiplier] = useState('');
  const [editConfidence, setEditConfidence] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleEdit = (row: LanguageMultiplier) => {
    setEditingId(row.id);
    setEditMultiplier(row.multiplier.toString());
    setEditConfidence(row.confidence);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditMultiplier('');
    setEditConfidence('');
  };

  const handleSave = async (id: number) => {
    const multiplierVal = parseFloat(editMultiplier);
    if (isNaN(multiplierVal) || multiplierVal < 0 || multiplierVal > 5) {
      toast.error('Multiplier must be between 0 and 5');
      return;
    }
    setIsSaving(true);
    try {
      await updateMultiplier({ id, multiplier: multiplierVal, confidence: editConfidence }).unwrap();
      toast.success('Multiplier updated');
      setEditingId(null);
    } catch {
      toast.error('Failed to update multiplier');
    } finally {
      setIsSaving(false);
    }
  };

  const formatEra = (eraKey: string): string => {
    const eraLabels: Record<string, string> = {
      'default': 'Default',
      'jp_pre_2003': 'Pre-2003',
      'jp_2003_2010': '2003–2010',
      'jp_2011_2018': '2011–2018',
      'jp_2019_plus': '2019+',
    };
    return eraLabels[eraKey] || eraKey;
  };

  if (isLoading) {
    return (
      <div className="controls-card">
        <div className="card-header">
          <h2>Language Pricing Model</h2>
        </div>
        <div className="card-content">
          <div className="loading-text">Loading...</div>
        </div>
      </div>
    );
  }

  // Group by language for readability
  const sorted = [...(multipliers || [])].sort((a, b) => {
    const langOrder = a.languageCode.localeCompare(b.languageCode);
    if (langOrder !== 0) return langOrder;
    const eraOrder = a.eraKey.localeCompare(b.eraKey);
    if (eraOrder !== 0) return eraOrder;
    const tierOrder = ['Staple', 'Mid', 'Low', 'Bulk'];
    return tierOrder.indexOf(a.tier) - tierOrder.indexOf(b.tier);
  });

  return (
    <div className="controls-card">
      <div className="card-header">
        <h2>Language Pricing Model</h2>
        <p className="card-description">
          Multipliers applied to English base price for non-English card valuations
        </p>
      </div>

      <div className="card-content">
        <div className="lang-multiplier-table-wrapper">
          <table className="lang-multiplier-table">
            <thead>
              <tr>
                <th>Language</th>
                <th>Era</th>
                <th>Tier</th>
                <th>Multiplier</th>
                <th>Confidence</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr key={row.id}>
                  <td>{getLanguageDisplayName(row.languageCode)}</td>
                  <td>{formatEra(row.eraKey)}</td>
                  <td>{row.tier}</td>
                  <td>
                    {editingId === row.id ? (
                      <input
                        type="number"
                        className="lang-multiplier-input"
                        value={editMultiplier}
                        onChange={(e) => setEditMultiplier(e.target.value)}
                        step="0.01"
                        min="0"
                        max="5"
                      />
                    ) : (
                      <span>{row.multiplier.toFixed(2)}x</span>
                    )}
                  </td>
                  <td>
                    {editingId === row.id ? (
                      <select
                        className="lang-multiplier-select"
                        value={editConfidence}
                        onChange={(e) => setEditConfidence(e.target.value)}
                      >
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    ) : (
                      <span className={`confidence-badge confidence-${row.confidence}`}>
                        {row.confidence}
                      </span>
                    )}
                  </td>
                  <td className="lang-multiplier-date">
                    {new Date(row.updatedAt).toLocaleDateString()}
                  </td>
                  <td>
                    {editingId === row.id ? (
                      <div className="lang-multiplier-actions">
                        <MagicButton
                          label={isSaving ? '...' : 'Save'}
                          color="purple"
                          onPress={() => handleSave(row.id)}
                          isDisabled={isSaving}
                          fullWidth={false}
                        />
                        <MagicButton
                          label="Cancel"
                          color="grey"
                          onPress={handleCancel}
                          isDisabled={isSaving}
                          fullWidth={false}
                        />
                      </div>
                    ) : (
                      <button className="lang-multiplier-edit-btn" onClick={() => handleEdit(row)}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LanguageMultiplierSection;
